import type { GeoJSON } from "geojson"
import type { CircleLayerSpecification, FillLayerSpecification, LineLayerSpecification } from "mapbox-gl"
import { memo, useEffect, useId, useMemo, useState } from "react"

import { useAppSelector } from "../../store"
import { selectMapStyle } from "../../store/selectors"
import { Layer, Source } from "../components"
import { MapSlotLayerId } from "../enums"
import { mapStyleColorMapping } from "../mappings"

interface GeoJsonLayerProps {
	file: File
}

export const GeoJsonLayer = memo((props: GeoJsonLayerProps) => {
	const { file } = props

	const id = useId()
	const mapStyle = useAppSelector(selectMapStyle)
	const color = mapStyleColorMapping[mapStyle]
	const [geoJson, setGeoJson] = useState<GeoJSON | null>(null)

	useEffect(() => {
		async function fetchData() {
			const asText = await file.text()
			const parsed = JSON.parse(asText) as GeoJSON
			setGeoJson(parsed)
		}
		void fetchData()
	}, [file])

	const circleLayerPaint = useMemo(() => {
		return {
			"circle-color": ["coalesce", ["get", "Color"], color],
			"circle-radius": 1,
		} satisfies CircleLayerSpecification["paint"]
	}, [color])

	const fillLayerPaint = useMemo(() => {
		return {
			"fill-color": ["coalesce", ["get", "Color"], color],
			"fill-opacity": 0.3,
		} satisfies FillLayerSpecification["paint"]
	}, [color])

	const lineLayerPaint = useMemo(() => {
		return {
			"line-color": ["coalesce", ["get", "Color"], color],
			"line-width": 1,
			"line-opacity": 0.8,
		} satisfies LineLayerSpecification["paint"]
	}, [color])

	if (!geoJson) return null

	return (
		<Source type="geojson" data={geoJson}>
			<Layer
				id={`${id}-circle-layer`}
				type="circle"
				paint={circleLayerPaint}
				beforeId={MapSlotLayerId.PROJECT_FILES}
			/>
			<Layer id={`${id}-fill-layer`} type="fill" paint={fillLayerPaint} beforeId={MapSlotLayerId.PROJECT_FILES} />
			<Layer id={`${id}-line-layer`} type="line" paint={lineLayerPaint} beforeId={MapSlotLayerId.PROJECT_FILES} />
		</Source>
	)
})
GeoJsonLayer.displayName = "GeoJsonLayer"
